// draftBatches.ts — groups parametric asset drafts for the admin review surface.
//
// Q-A settled that a generated variant IS a draft row rather than a separate
// `product_variants` record, which keeps the shipped approval path unchanged but
// means a generated matrix arrives as N loose rows. M2 added `batch_id` to tie
// them together; this module turns a flat draft list into reviewable units.
//
// Dependency-free so `npx tsx src/lib/draftBatches.test.ts` runs without vite.

export type DraftStatus = "pending_review" | "approved" | "rejected";

/** The draft fields grouping depends on. Structural so the page's richer row
 *  type satisfies it without a shared import. */
export interface GroupableDraft {
  id: string;
  batch_id: string | null;
  product_name: string;
  sku: string;
  status: DraftStatus;
  staging_glb_url: string | null;
  created_at: string;
}

export interface DraftGroup<T extends GroupableDraft = GroupableDraft> {
  /** Null for a standalone draft — see isBatch. */
  batchId: string | null;
  /** True only for a real multi-draft batch, so the UI can render a singleton
   *  exactly as it did before this module existed. */
  isBatch: boolean;
  label: string;
  drafts: T[];
  counts: { pending: number; approved: number; rejected: number };
  /** Pending drafts that a bulk approve may actually act on. Mirrors the
   *  per-draft button's rule: no staged GLB, no approval. Bulk actions that
   *  quietly relax a per-item guard are how guards stop meaning anything. */
  approvable: T[];
}

/** Newest member first, matching the page's `order=created_at.desc`. */
function newestAt(drafts: GroupableDraft[]): string {
  return drafts.reduce((max, d) => (d.created_at > max ? d.created_at : max), "");
}

function describe(drafts: GroupableDraft[]): string {
  const names = new Set(drafts.map((d) => d.product_name));
  if (names.size === 1) return drafts[0].product_name;
  return `${names.size} products`;
}

/**
 * Groups drafts by `batch_id`, leaving unbatched drafts standalone.
 *
 * A draft with no batch_id is its own group rather than being pooled into an
 * "ungrouped" bucket: the shipped fireplace flow creates drafts one at a time,
 * and pooling them would attach a bulk-approve button spanning unrelated
 * products — a destructive action nobody asked for.
 */
export function groupDrafts<T extends GroupableDraft>(drafts: T[]): DraftGroup<T>[] {
  const batched = new Map<string, T[]>();
  const groups: DraftGroup<T>[] = [];

  for (const d of drafts) {
    if (d.batch_id === null) {
      groups.push(buildGroup(null, [d]));
    } else {
      const bucket = batched.get(d.batch_id);
      if (bucket) bucket.push(d);
      else batched.set(d.batch_id, [d]);
    }
  }

  for (const [batchId, members] of batched) groups.push(buildGroup(batchId, members));

  return groups.sort((a, b) => (newestAt(a.drafts) < newestAt(b.drafts) ? 1 : -1));
}

function buildGroup<T extends GroupableDraft>(batchId: string | null, drafts: T[]): DraftGroup<T> {
  const pending = drafts.filter((d) => d.status === "pending_review");
  return {
    batchId,
    isBatch: batchId !== null && drafts.length > 1,
    label: describe(drafts),
    drafts,
    counts: {
      pending:  pending.length,
      approved: drafts.filter((d) => d.status === "approved").length,
      rejected: drafts.filter((d) => d.status === "rejected").length,
    },
    approvable: pending.filter((d) => d.staging_glb_url !== null),
  };
}
